import { useEffect, useState } from "react";
import { isDataUrl, isS3Key, resolveFileUrl } from "../utils/fileUpload";

/**
 * In-memory cache for presigned URLs.
 * Keys: S3 object keys; values: { url, expiresAt }.
 * TTL is 50 minutes (presigned URLs expire at 60).
 */
const cache = new Map<string, { url: string; expiresAt: number }>();
const CACHE_TTL_MS = 50 * 60 * 1000;

export interface UseSignedUrlResult {
	url: string | null;
	loading: boolean;
	error: string | null;
}

/**
 * Resolves a stored file value (S3 key, data URL, or presigned URL) to a
 * viewable URL. S3 keys are resolved lazily via `GET /api/documents/signed-url/:key`
 * with a 50-minute in-memory cache.
 *
 * Legacy data URLs are returned directly (no API call).
 */
export function useSignedUrl(
	keyOrDataUrl: string | undefined,
): UseSignedUrlResult {
	const [url, setUrl] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!keyOrDataUrl) {
			setUrl(null);
			setLoading(false);
			setError(null);
			return;
		}

		// Data URLs are directly usable — no API call needed
		if (isDataUrl(keyOrDataUrl)) {
			setUrl(keyOrDataUrl);
			setLoading(false);
			return;
		}

		// S3 key — resolve to presigned URL (with cache)
		if (isS3Key(keyOrDataUrl)) {
			const cached = cache.get(keyOrDataUrl);
			if (cached && cached.expiresAt > Date.now()) {
				setUrl(cached.url);
				setLoading(false);
				return;
			}

			let cancelled = false;
			setLoading(true);
			setError(null);

			resolveFileUrl(keyOrDataUrl)
				.then((resolved) => {
					if (cancelled) return;
					if (resolved) {
						cache.set(keyOrDataUrl, {
							url: resolved,
							expiresAt: Date.now() + CACHE_TTL_MS,
						});
					}
					setUrl(resolved);
				})
				.catch((err) => {
					if (cancelled) return;
					setError(
						err instanceof Error ? err.message : "Failed to load file URL",
					);
				})
				.finally(() => {
					if (!cancelled) setLoading(false);
				});

			return () => {
				cancelled = true;
			};
		}

		// Unknown format (legacy presigned URL, http URL) — pass through
		setUrl(keyOrDataUrl);
		setLoading(false);
	}, [keyOrDataUrl]);

	return { url, loading, error };
}
